<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TestCampaignForm from './TestCampaignForm.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'tested']);
const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const uiFlags = getters['wgptCampaigns/getUIFlags'];
const stack = ref([]);
const canDismiss = computed(() => stack.value.length === 0);

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const isContactEmpty = computed(() => {
  if (!formRef.value) return true;
  const { form } = formRef.value;
  return !form.contact_ids.length;
});

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;

    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate, form } = formRef.value;

    if (!(await validate())) return;

    const testOptions = {
      id: props.selectedCampaign.id,
      execution_config: {
        contact_ids: form.contact_ids,
      },
    };
    await store.dispatch('wgptCampaigns/test', testOptions);

    useAlert(t('WGPT_CAMPAIGNS.TEST.API.SUCCESS'));
    emit('tested');
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('WGPT_CAMPAIGNS.TEST.API.ERROR');
    useAlert(errorMessage);
  }
};

useEmitter('WGPT_DROPDOWN_TOGGLE', ({ id, state }) => {
  if (state) {
    stack.value = [...stack.value, id];
  } else {
    stack.value = stack.value.filter(item => item !== id);
  }
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_CAMPAIGNS.TEST.TITLE')"
    :description="t('WGPT_CAMPAIGNS.TEST.DESCRIPTION')"
    :confirm-button-label="t('WGPT_CAMPAIGNS.TEST.CONFIRM')"
    :cancel-button-label="t('WGPT_CAMPAIGNS.TEST.CLOSE')"
    :can-dismiss="canDismiss"
    :is-loading="uiFlags.isTesting"
    :disable-confirm-button="uiFlags.isTesting || isContactEmpty"
    overflow-y-auto
    @confirm="handleSubmit"
  >
    <TestCampaignForm v-if="isOpen" ref="formRef" @select="handleSubmit" />
  </Dialog>
</template>
